import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * PageHead — per-route <title>, <meta description>, canonical, and robots.
 *
 * Why the manual DOM effect: index.html emits homepage-flavoured canonical
 * and robots tags server-side. react-helmet-async APPENDS its own tags but
 * does not remove non-Helmet-managed tags of the same type, so Google would
 * see two canonicals / two robots. We therefore manually remove any prior
 * canonical or robots elements when this component provides its own — that
 * way the DOM at steady state has exactly one of each.
 *
 * SPA/social-crawler caveat: only what lives in /app/frontend/public/index.html
 * is visible to non-JS social crawlers (LinkedIn, Facebook, iMessage, Slack,
 * Discord, WhatsApp). Anything set here mutates the head AFTER React runs and
 * is therefore only picked up by browsers + Googlebot (which renders JS).
 * Route-specific social previews require prerendering/SSR.
 */
export default function PageHead({ title, description, canonical, noindex = false }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    // If we're supplying a canonical, drop every existing canonical link.
    if (canonical) {
      document
        .querySelectorAll('link[rel="canonical"]')
        .forEach((el) => {
          if (!el.hasAttribute("data-pagehead")) el.remove();
        });
    }
    // If we're supplying a robots directive, drop every existing one.
    if (noindex) {
      document
        .querySelectorAll('meta[name="robots"]')
        .forEach((el) => {
          if (!el.hasAttribute("data-pagehead")) el.remove();
        });
    }
  }, [canonical, noindex]);

  return (
    <Helmet prioritizeSeoTags>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      {canonical ? <link rel="canonical" href={canonical} data-pagehead="1" /> : null}
      {noindex ? <meta name="robots" content="noindex,nofollow" data-pagehead="1" /> : null}
    </Helmet>
  );
}
